import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Plus } from "lucide-react"
import { toast } from "sonner"
import axiosInstance from "../../utils/axios/axiosConfig"
import StarRating from "./StarRating"

// shared inputs
import { TextInput, TextArea, SelectSearch } from "../../components"

const safeJsonParseArray = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const getValue = (v) => (v && typeof v === "object" && "target" in v ? v.target.value : v)

const ReviewModal = ({ isOpen, onClose, setReviewToUpdate, ReviewToUpdate, setDone, done }) => {
  const [busy, setBusy] = useState(false)
  const [availableProducts, setAvailableProducts] = useState([])

  const [form, setForm] = useState({
    images: [],
    comment: "",
    product_id: "",
    rating: 0,
    author: "",
  })

  // lock scroll
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev || "unset"
    }
  }, [isOpen])

  // load products + hydrate form when opened
  useEffect(() => {
    if (!isOpen) return

    const hydrate = () => {
      if (ReviewToUpdate?.id) {
        setForm({
          images: safeJsonParseArray(ReviewToUpdate.images),
          product_id: ReviewToUpdate.product_id || "",
          comment: ReviewToUpdate.comment || "",
          rating: Number(ReviewToUpdate.rating || 0),
          author: ReviewToUpdate.author || "",
        })
      } else {
        setForm({ images: [], comment: "", product_id: "", rating: 0, author: "" })
      }
    }

    const fetchProducts = async () => {
      try {
        setBusy(true)
        const res = await axiosInstance.post("/products/search")
        setAvailableProducts(res?.data?.data || [])
      } catch (err) {
        toast.error(err?.response?.data?.message || "Error fetching products")
      } finally {
        setBusy(false)
      }
    }

    hydrate()
    fetchProducts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ReviewToUpdate?.id])

  const productOptions = useMemo(
    () =>
      (availableProducts || []).map((p) => ({
        label: p?.name,
        value: p?.id,
      })),
    [availableProducts],
  )

  const setField = (name, valueOrEvent) => {
    setForm((prev) => ({ ...prev, [name]: getValue(valueOrEvent) }))
  }

  const close = () => {
    setForm({ images: [], comment: "", product_id: "", rating: 0, author: "" })
    setReviewToUpdate?.(null)
    setDone?.(!done)
    onClose?.()
  }

  const handleImageChange = async (e) => {
    const files = Array.from(e?.target?.files || [])
    if (!files.length) return

    try {
      const base64Images = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.onerror = reject
              reader.readAsDataURL(file)
            }),
        ),
      )

      setForm((prev) => ({ ...prev, images: [...(prev.images || []), ...base64Images] }))
    } catch (err) {
      console.error(err)
      toast.error("Failed to read images")
    } finally {
      // allow re-uploading same file name
      if (e?.target) e.target.value = ""
    }
  }

  const removeImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (busy) return

    if (!form.comment?.trim()) return toast.error("Please write a comment")
    if (!form.rating) return toast.error("Please select a rating")
    if (!form.product_id) return toast.error("Please select a product")

    try {
      setBusy(true)

      const payload = {
        images: form.images || [],
        comment: form.comment,
        product_id: form.product_id,
        rating: form.rating,
        author: form.author,
      }

      const res = ReviewToUpdate?.id
        ? await axiosInstance.put(`/reviews/update/${ReviewToUpdate.id}`, payload)
        : await axiosInstance.post(`/reviews/create`, payload)

      toast.success(res?.data?.message || "Saved")
      close()
    } catch (err) {
      toast.error(err?.response?.data?.message || "An error occurred")
    } finally {
      setBusy(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex justify-end"
          onMouseDown={(e) => {
            // click outside closes
            if (e.target === e.currentTarget) close()
          }}
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
            className="w-full max-w-md h-full overflow-y-auto bg-white dark:bg-neutral-800"
            role="dialog"
            aria-modal="true"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-neutral-800 dark:text-white">
                  {ReviewToUpdate?.id ? "Update" : "Add"} Review
                </h2>

                <button
                  type="button"
                  onClick={close}
                  className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Images */}
                <div className="rounded-lg bg-white dark:bg-neutral-800">
                  <label className="block mb-3 text-start text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Upload Images
                  </label>

                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-1 block w-full text-sm text-neutral-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />

                  {(form.images || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {form.images.map((img, index) => (
                        <div key={index} className="relative">
                          <img
                            src={img || "/placeholder.svg"}
                            alt={`Review ${index + 1}`}
                            className="h-28 w-28 object-cover rounded-xl border border-neutral-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            aria-label="Remove image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Rating */}
                <div>
                  <label className="block mb-3 text-start text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Rating
                  </label>
                  <StarRating rating={form.rating} onRatingChange={(v) => setField("rating", v)} />
                </div>

                {/* Author */}
                <TextInput
                  label="Author"
                  name="author"
                  value={form.author}
                  onChange={(e) => setField("author", e)}
                  placeholder="John Doe"
                />

                {/* Comment */}
                <TextArea
                  label="Comment"
                  name="comment"
                  value={form.comment}
                  onChange={(e) => setField("comment", e)}
                  rows={3}
                  placeholder="Write a short review..."
                />

                {/* Product */}
                <SelectSearch
                  label="Product"
                  name="product_id"
                  value={form.product_id}
                  onChange={(v) => setField("product_id", v)}
                  options={productOptions}
                  placeholder="Select product"
                />

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={busy}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium shadow-sm text-white bg-[#2B2B35] rounded-lg hover:bg-cyan-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {busy ? (
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <Plus className="w-5 h-5 mr-2" />
                    )}
                    {busy ? "Saving..." : "Save Review"}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ReviewModal
